import React from 'react'
import { useTranslation } from 'react-i18next'
import { Split } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatMoney } from '../lib/money'
import type { CostContract, CostContractLineView } from '../types'

interface Props { contract: CostContract; lines: CostContractLineView[]; currency: string; locale: string; canEdit: boolean; derived: boolean; onEdit: () => void }

const ContractLinesCard: React.FC<Props> = ({ contract, lines, currency, locale, canEdit, derived, onEdit }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_contract_lines_title')}</CardTitle>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Split className="h-4 w-4" />
            {t('CostControl_contract_lines_edit')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {derived && <p className="mb-2 text-sm text-muted-foreground">{t('CostControl_price_lines_derived')}</p>}
        {contract.kind === 'MAIN' && <p className="text-sm text-muted-foreground">{t('CostControl_contract_lines_main')}</p>}
        {contract.kind === 'SUB' && lines.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_contract_lines_empty')}</p>}
        {contract.kind === 'SUB' && lines.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('CostControl_lines_item')}</TableHead>
                <TableHead>{t('CostControl_lines_note')}</TableHead>
                <TableHead className="w-44 text-right">{t('CostControl_lines_amount')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>
                    {l.path && <div className="text-xs text-muted-foreground">{l.path}</div>}
                    <div className="flex flex-wrap items-center gap-2">
                      <span>{l.nodeCode ? `${l.nodeCode} · ${l.nodeName}` : l.nodeName}</span>
                      {l.provisional && <Badge variant="secondary">{t('CostControl_provisional_badge')}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{l.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(l.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2} className="font-semibold">{t('CostControl_contract_lines_total')}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">{money(contract.totals.value)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
export default ContractLinesCard
